"use client";

import { Suspense, use, useEffect } from "react";
import { type FlowErrorProps, getTenantErrorPage } from "@tillix/flux/client";
import { useT } from "@tillix/i18n/react";
import { useTenant } from "@tillix/tenants/react";
import { Button } from "@tillix/ui-atoms/button";
import { observer } from "mobx-react-lite";

/**
 * Error boundary shared by every flux flow (`/f/*`). A throw during a flow's
 * server render — e.g. an upstream SAP response failing schema validation —
 * lands here instead of Next's bare "server-side exception" page. It sits
 * inside the flux layout, so the tenant header/footer stay up and the
 * customer gets a retry rather than a dead page. Renders the tenant's
 * `error.tsx` from `@tillix/flux` when it supplies one, else the default.
 */
const FlowError = observer(function _FlowError({
  error,
  reset,
}: FlowErrorProps) {
  const { id: tenantId } = useTenant();

  useEffect(() => {
    // The server already logged the full error; the digest lets support
    // correlate what the customer saw with that log line.
    console.error("[flux] flow render failed", error.digest, error);
  }, [error]);

  return (
    <Suspense fallback={null}>
      <TenantFlowError error={error} reset={reset} tenantId={tenantId} />
    </Suspense>
  );
});

export default FlowError;

const TenantFlowError = observer(function _TenantFlowError({
  tenantId,
  error,
  reset,
}: FlowErrorProps & { tenantId: string }) {
  const TenantErrorPage = use(getTenantErrorPage(tenantId));
  if (TenantErrorPage) return <TenantErrorPage error={error} reset={reset} />;
  return <DefaultFlowError error={error} reset={reset} />;
});

const DefaultFlowError = observer(function _DefaultFlowError({
  reset,
}: FlowErrorProps) {
  const { t: tErrors } = useT("errors");
  const { t: tUi } = useT();

  return (
    <main className="mx-auto flex w-full max-w-4xl flex-col items-center gap-4 px-8 py-24 text-center">
      <h1 className="font-semibold text-2xl">
        {tErrors("common.somethingWentWrong")}
      </h1>
      <p className="text-muted-foreground">
        {tErrors("common.couldNotLoadPage")} {tErrors("common.tryAgainLater")}
      </p>
      <Button onClick={reset} type="button">
        {tUi("application.tryAgain")}
      </Button>
    </main>
  );
});
